/*
 * Shared diagnostics identity helpers (SHER-320 / SHER-323).
 *
 * Single source for the small, side-effect-free identity primitives that the
 * telemetry and breadcrumb pipelines both need: API base URL, app version /
 * git sha, tenant key, the auth JWT and its decoded claims, plus a uuid-v4
 * generator. Previously each pipeline carried its own byte-for-byte copy of
 * these, so a behaviour fix had to be applied in two (or three) places.
 *
 * Everything here is fully guarded: any failure degrades to a null/empty
 * fallback so instrumentation can never break a request or the app.
 */

/** uuid v4. Prefers crypto.randomUUID; RFC4122-ish Math.random fallback. */
function safeRandomUUID() {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
    } catch (_) { /* ignore */ }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/** API base URL from VITE_APP_API_URL (trailing slash stripped). '' if absent. */
function apiBaseUrl() {
    try {
        if (typeof import.meta !== 'undefined' && import.meta && import.meta.env) {
            const v = import.meta.env.VITE_APP_API_URL;
            if (typeof v === 'string' && v) return v.replace(/\/$/, '');
        }
    } catch (_) { /* ignore */ }
    return '';
}

/** Build-time app version (__APP_VERSION__) or null. */
function appVersion() {
    try { return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null; } catch (_) { return null; }
}

/** Build-time git sha (__GIT_SHA__) or null. */
function appGitSha() {
    try { return typeof __GIT_SHA__ !== 'undefined' ? __GIT_SHA__ : null; } catch (_) { return null; }
}

/**
 * Tenant key. Prefers an explicit VITE_APP_TENANT, then the per-tenant slug
 * derived from the API host, then the product flavour (HOSPITAL / dashboard).
 */
function tenant() {
    try {
        if (typeof import.meta !== 'undefined' && import.meta && import.meta.env) {
            const explicit = import.meta.env.VITE_APP_TENANT;
            if (typeof explicit === 'string' && explicit) return explicit;
            const apiUrl = import.meta.env.VITE_APP_API_URL;
            if (typeof apiUrl === 'string' && apiUrl) {
                try {
                    const host = new URL(apiUrl).host;
                    if (host) return host;
                } catch (_) { /* ignore */ }
            }
            const product = import.meta.env.VITE_APP_PRODUCT;
            if (typeof product === 'string' && product) return product;
        }
    } catch (_) { /* ignore */ }
    return 'unknown';
}

/** The auth JWT from localStorage ('jwt' key), or null. */
function jwt() {
    try {
        return typeof window !== 'undefined' ? (window.localStorage?.getItem('jwt') || null) : null;
    } catch (_) { return null; }
}

/** Decode a JWT payload's claims, never throwing. Returns {} on any failure. */
function decodeJwtClaims(token) {
    try {
        if (!token || typeof token !== 'string') return {};
        const parts = token.split('.');
        if (parts.length < 2) return {};
        const body = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(body);
    } catch (_) {
        return {};
    }
}

/** researcherUuid from the JWT payload (`uuid` claim). No PII beyond the uuid. */
function researcherUuid() {
    const claims = decodeJwtClaims(jwt());
    return typeof claims.uuid === 'string' ? claims.uuid : null;
}

export {
    safeRandomUUID,
    apiBaseUrl,
    appVersion,
    appGitSha,
    tenant,
    jwt,
    decodeJwtClaims,
    researcherUuid,
};
